<template>
  <div data-testid="sso-domains-panel">
    <div class="d-flex align-center mb-2">
      <div class="subtitle-1" data-testid="sso-domains-panel__title">Domains / membership</div>
      <v-spacer />
      <v-btn
        v-if="canManageDedicatedDomains"
        small
        color="primary"
        class="primaryBtn"
        :disabled="saving"
        data-testid="sso-domains-panel__add"
        @click="showAdd = true"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add domain
      </v-btn>
    </div>

    <v-alert
      v-if="isShared && !isAppHostSsoAdminClient"
      type="info"
      text
      dense
      border="left"
      class="mb-3"
    >
      Users sign in on {{ appHostDisplay }}. A dedicated hostname is not required
      for this client. To include this client on the app-host login allowlist,
      manage <code>clientIds</code> from Consark Development.
    </v-alert>

    <template v-if="isAppHostSsoAdminClient">
      <div class="mb-2 body-2 font-weight-medium">App-host clientIds</div>
      <div class="sso-domains-panel__client-id-row d-flex align-center mb-3">
        <v-text-field
          v-model="clientIdToAdd"
          label="Client ObjectId to add"
          outlined
          dense
          hide-details
          class="mr-2"
          :disabled="saving || !appHostDomainId"
          data-testid="sso-domains-panel__client-id-input"
        />
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!clientIdToAdd || !appHostDomainId"
          data-testid="sso-domains-panel__client-id-add"
          @click="addMembership"
        >
          Add
        </v-btn>
      </div>
      <div v-if="(clientIds || []).length" class="mb-4">
        <div
          v-for="cid in clientIds"
          :key="cid"
          class="hoverable"
        >
          <div class="d-flex align-center py-1 px-2">
            <code>{{ cid }}</code>
            <v-spacer />
            <v-btn
              icon
              small
              :disabled="saving"
              @click="removeMembership(cid)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider />
        </div>
      </div>
      <div v-else class="caption grey--text mb-4">No clientIds on the app-host domain yet.</div>
    </template>

    <div v-if="(domains || []).length" class="mb-2">
      <div
        v-for="domain in domains"
        :key="domain._id || domain.id || domain.hostname"
        class="hoverable"
      >
        <div class="d-flex align-center py-2 px-2">
          <div>
            <div class="font-weight-bold">{{ domain.hostname }}</div>
            <div class="caption grey--text">
              {{ domain.hostingMode || '—' }}
              · {{ domain.lifecycle || '—' }}
              <span v-if="domain.isPrimary"> · primary</span>
              <span v-if="domain.slug"> · slug {{ domain.slug }}</span>
            </div>
          </div>
          <v-spacer />
          <v-chip
            v-if="domain.lifecycle === 'RETIRED'"
            small
            label
            color="grey"
            class="white--text mr-2"
          >
            Retired
          </v-chip>
          <v-btn
            v-if="canManageDedicatedDomains && domain.lifecycle !== 'RETIRED'"
            icon
            small
            :disabled="saving"
            @click="askRetire(domain)"
          >
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <v-divider />
      </div>
    </div>
    <div v-else-if="!isShared || isAppHostSsoAdminClient" class="title grey--text my-4 text-center">
      No domains configured
    </div>

    <v-dialog
      v-model="showAdd"
      content-class="sso-domains-panel__add-dialog"
      persistent
      data-testid="sso-domains-panel__add-dialog"
    >
      <v-card :loading="saving" :disabled="saving" loader-height="2">
        <div
          class="sso-domains-panel__add-dialog-header primary white--text d-flex align-center px-4"
          data-testid="sso-domains-panel__add-dialog-header"
        >
          <span class="subtitle-1 white--text">Add dedicated domain</span>
          <v-spacer />
          <v-btn
            icon
            class="white--text"
            data-testid="sso-domains-panel__add-dialog-close"
            @click="showAdd = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="newHostname"
            label="Hostname*"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            data-testid="sso-domains-panel__hostname"
          />
          <v-text-field
            v-model="newSlug"
            label="Slug*"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            data-testid="sso-domains-panel__slug"
          />
          <v-switch
            v-model="newIsPrimary"
            label="Primary domain"
            inset
            color="success"
            dense
            hide-details
            class="mt-0"
            data-testid="sso-domains-panel__is-primary"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text data-testid="sso-domains-panel__add-dialog-cancel" @click="showAdd = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            data-testid="sso-domains-panel__add-dialog-submit"
            @click="createDomain"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <RetireConfirmDialog
      v-model="retireDialog"
      title="Retire domain"
      message="Retiring a domain disables login on that hostname."
      :loading="saving"
      show-reserve-days
      @confirm="confirmRetire"
    />
  </div>
</template>

<script>
import { getAppHost } from '@/plugins/hostMode';
import RetireConfirmDialog from './RetireConfirmDialog.vue';

export default {
  name: 'DomainsPanel',
  components: { RetireConfirmDialog },
  props: {
    domains: { type: Array, default: () => [] },
    clientIds: { type: Array, default: () => [] },
    appHostDomainId: { type: String, default: '' },
    isShared: { type: Boolean, default: true },
    isAppHostSsoAdminClient: { type: Boolean, default: false },
    canManageDedicatedDomains: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      clientIdToAdd: '',
      showAdd: false,
      newHostname: '',
      newSlug: '',
      newIsPrimary: true,
      retireDialog: false,
      retireTarget: null,
    };
  },
  computed: {
    appHostDisplay() {
      return getAppHost() || 'the Consark app host';
    },
  },
  methods: {
    addMembership() {
      const targetClientId = String(this.clientIdToAdd || '').trim();
      if (!targetClientId || !this.appHostDomainId) return;
      this.$emit('add-client-id', {
        domainId: this.appHostDomainId,
        targetClientId,
      });
      this.clientIdToAdd = '';
    },
    removeMembership(targetClientId) {
      if (!this.appHostDomainId || !targetClientId) return;
      this.$emit('remove-client-id', {
        domainId: this.appHostDomainId,
        targetClientId,
      });
    },
    createDomain() {
      const hostname = String(this.newHostname || '').trim().toLowerCase();
      const slug = String(this.newSlug || '').trim().toLowerCase();
      if (!hostname || !slug) {
        this.$store.commit('sbError', 'Hostname and slug are required');
        return;
      }
      this.$emit('add-domain', {
        hostname,
        slug,
        isPrimary: this.newIsPrimary,
        hostingMode: 'DEDICATED_SUBDOMAIN',
        authPolicy: 'HYBRID',
      });
      this.showAdd = false;
      this.newHostname = '';
      this.newSlug = '';
      this.newIsPrimary = true;
    },
    askRetire(domain) {
      this.retireTarget = domain;
      this.retireDialog = true;
    },
    confirmRetire({ reason, reserveDays }) {
      if (!this.retireTarget) return;
      this.$emit('retire-domain', {
        domainId: this.retireTarget._id || this.retireTarget.id,
        reason,
        reserveDays,
      });
      this.retireDialog = false;
      this.retireTarget = null;
    },
  },
};
</script>

<style lang="scss">
@import './DomainsPanel.scss';
</style>
